/**
 * AppRouter — top-level router that dispatches to:
 *   - /login                 → LoginPage
 *   - /invite/:token         → InviteAcceptPage
 *   - /platform              → PlatformConsole (platform root only)
 *   - /*                     → tenant app (existing App.js)
 *
 * Also handles the initial hydration redirect so that:
 *   - unauthenticated users → /login
 *   - platform root users   → /platform
 *   - tenant users          → /
 */
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PlatformProvider } from './context/PlatformContext';
import PlatformActiveBanner from './components/PlatformActiveBanner';
import { FeatureProvider } from './context/FeatureContext';
import TenantProvider from './context/TenantContext';
import LoginPage from './components/auth/LoginPage';
import InviteAcceptPage from './components/auth/InviteAcceptPage';
import PlatformConsole from './components/PlatformConsole';
import FoundryWorkspace from './components/FoundryWorkspace';
import SalesDemoWorkspace from './components/SalesDemoWorkspace';
import SettingsPage from './components/SettingsPage';
import ProfilePage from './components/ProfilePage';
import InvestigationViewPage from './components/InvestigationViewPage';
import UserManagementPage from './components/UserManagementPage';
import HelpReadmePage from './components/help/HelpReadmePage';
import HelpAboutPage from './components/help/HelpAboutPage';
import HelpLicencePage from './components/help/HelpLicencePage';
import HelpContactPage from './components/help/HelpContactPage';
import App from './App';
import PwaUpdateNotice from './components/PwaUpdateNotice';

function AuthLoadingScreen() {
  return (
    <div className="auth-shell" data-testid="auth-loading">
      <div className="auth-card">
        <p className="auth-subtitle">Loading QUOD-Finder…</p>
      </div>
    </div>
  );
}

function postLoginPath(u) {
  if (!u) return '/login';
  if (u.is_platform_root) return '/platform';
  if (u.role === 'sales_demo' && u.tenant_slug) return `/t/${u.tenant_slug}/sales-demo`;
  if (u.role === 'sales_demo') return '/sales-demo';
  if (u.tenant_slug) return `/t/${u.tenant_slug}`;
  return '/';   // legacy single-tenant fallback
}

function RequireAuth({ children, allow }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <AuthLoadingScreen />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (allow && !allow(user)) {
    return <Navigate to={postLoginPath(user)} replace />;
  }
  return children;
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (user) return <Navigate to={postLoginPath(user)} replace />;
  return <LoginPage />;
}

/**
 * Tenant-scoped layout. Mounts the TenantProvider (which handles the
 * 404 / not-found render) and then renders the nested routes via
 * <Outlet/>. Everything inside is already RBAC-protected upstream by
 * RequireAuth on the parent route.
 */
function TenantLayout() {
  return (
    <TenantProvider>
      <Outlet />
    </TenantProvider>
  );
}

function SalesDemoTenantRoute() {
  const { user, loading } = useAuth();
  const { tenantSlug } = useParams();
  if (loading) return <AuthLoadingScreen />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'sales_demo' || user.tenant_slug !== tenantSlug) {
    return <Navigate to={postLoginPath(user)} replace />;
  }
  return (
    <TenantProvider>
      <SalesDemoWorkspace />
    </TenantProvider>
  );
}

const isOrdinaryFinderUser = (u) => u.role !== 'sales_demo';

function Router() {
  return (
    <>
      <PlatformActiveBanner />
      <PwaUpdateNotice />
      <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/invite/:token" element={<InviteAcceptPage />} />
      <Route
        path="/sales-demo"
        element={
          <RequireAuth allow={(u) => u.role === 'sales_demo'}>
            <SalesDemoWorkspace />
          </RequireAuth>
        }
      />
      <Route path="/t/:tenantSlug/sales-demo" element={<SalesDemoTenantRoute />} />
      <Route
        path="/platform/foundry"
        element={
          <RequireAuth allow={(u) => u.is_platform_root}>
            <FoundryWorkspace />
          </RequireAuth>
        }
      />
      <Route
        path="/platform"
        element={
          <RequireAuth allow={(u) => u.is_platform_root}>
            <PlatformConsole />
          </RequireAuth>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAuth allow={isOrdinaryFinderUser}>
            <SettingsPage />
          </RequireAuth>
        }
      />
      <Route
        path="/profile"
        element={
          <RequireAuth allow={isOrdinaryFinderUser}>
            <ProfilePage />
          </RequireAuth>
        }
      />
      <Route
        path="/investigation/:investigationId"
        element={
          <RequireAuth allow={isOrdinaryFinderUser}>
            <InvestigationViewPage />
          </RequireAuth>
        }
      />
      <Route
        path="/users"
        element={
          <RequireAuth
            allow={(u) =>
              u.is_platform_root ||
              (Array.isArray(u.permissions) && u.permissions.includes('user:list'))
            }
          >
            <UserManagementPage />
          </RequireAuth>
        }
      />
      <Route path="/help/readme"  element={<RequireAuth allow={isOrdinaryFinderUser}><HelpReadmePage  /></RequireAuth>} />
      <Route path="/help/about"   element={<RequireAuth allow={isOrdinaryFinderUser}><HelpAboutPage   /></RequireAuth>} />
      <Route path="/help/licence" element={<RequireAuth allow={isOrdinaryFinderUser}><HelpLicencePage /></RequireAuth>} />
      <Route path="/help/contact" element={<RequireAuth allow={isOrdinaryFinderUser}><HelpContactPage /></RequireAuth>} />
      <Route
        path="/*"
        element={
          <RequireAuth allow={isOrdinaryFinderUser}>
            <App />
          </RequireAuth>
        }
      />

      {/*
        Path-based tenant routes for MVP multi-tenant launch.
        Mirrors the non-prefixed routes so /t/<slug>/... behaves
        identically to /... but wrapped in the TenantProvider.
      */}
      <Route
        path="/t/:tenantSlug"
        element={
          <RequireAuth allow={isOrdinaryFinderUser}>
            <TenantLayout />
          </RequireAuth>
        }
      >
        <Route index element={<App />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="investigation/:investigationId" element={<InvestigationViewPage />} />
        <Route
          path="users"
          element={
            <RequireAuth
              allow={(u) =>
                u.is_platform_root ||
                (Array.isArray(u.permissions) && u.permissions.includes('user:list'))
              }
            >
              <UserManagementPage />
            </RequireAuth>
          }
        />
        <Route path="*" element={<App />} />
      </Route>

      {/* Help routes also available under /t/<slug>/help/* for tenant-scoped URLs */}
      <Route
        path="/t/:tenantSlug/help"
        element={
          <RequireAuth allow={isOrdinaryFinderUser}>
            <TenantLayout />
          </RequireAuth>
        }
      >
        <Route path="readme"  element={<HelpReadmePage  />} />
        <Route path="about"   element={<HelpAboutPage   />} />
        <Route path="licence" element={<HelpLicencePage />} />
        <Route path="contact" element={<HelpContactPage />} />
      </Route>
    </Routes>
    </>
  );
}

export default function AppRouter() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PlatformProvider>
          <FeatureProvider>
            <Router />
          </FeatureProvider>
        </PlatformProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
